import { safeExternalUrlSchema } from "../../network/safe-external-url.schema";

/**
 * The canonical form of an MCP server URL — the value `McpServer.url` stores, and
 * the key `@@unique([organizationId, url])` compares.
 *
 * Canonical means: scheme and host lowercased and a default port dropped (the
 * WHATWG parser does both), the fragment dropped, and every trailing `/` removed
 * from the path, so `https://Mcp.Example.com:443/mcp/` and
 * `https://mcp.example.com/mcp` are one server. That last rule is the MCP
 * specification's canonical server URI, which prefers the form without a trailing
 * slash. The query string is kept: it can legitimately select a server.
 *
 * Expects a URL `mcpServerUrlSchema` has already accepted.
 */
export function canonicalizeMcpServerUrl(url: string): string {
  const parsed = new URL(url);
  const path = parsed.pathname.replace(/\/+$/, "");
  return `${parsed.protocol}//${parsed.host}${path}${parsed.search}`;
}

/**
 * The write boundary for an MCP server URL: HTTPS, not a private or internal
 * address, no userinfo, and canonicalised on the way out.
 *
 * It composes `safeExternalUrlSchema` rather than re-declaring its refinements —
 * that schema's header forbids a second copy. Judging the URL here does NOT make
 * dialling it safe: DNS can answer differently at connect time and a redirect is
 * never inspected, so every request to the server still goes through the
 * egress-guarded fetch.
 *
 * Userinfo is refused because `url` is stored unencrypted and echoed to operators:
 * a server that needs a secret takes it through `McpAuthMode.HEADER`, whose value
 * is encrypted.
 */
export const mcpServerUrlSchema = safeExternalUrlSchema
  .refine(
    (url) => {
      // 🚨 THE `try` IS LOAD-BEARING, AND ITS ABSENCE WAS A 500. Zod runs every
      // refinement in a chain even after an earlier check has already failed, so
      // this one is handed the raw input when `.url()` refused it — and
      // `new URL("")` throws a `TypeError`, not a `ZodError`. That escapes
      // `safeParse` entirely: `RegisterMcpServerBodySchema.parse(body)` threw it,
      // `ZodExceptionFilter` never saw it, and a blank or malformed `url` in a
      // registration answered 500 instead of the 400 that names the field.
      //
      // `false` rather than a rethrow: a URL this refinement cannot even parse has
      // certainly not proved it carries no userinfo, and the earlier `.url()`
      // failure is already the issue the caller will read. The sibling refinement
      // in `safeExternalUrlSchema` guards its own `new URL` the same way, for the
      // same reason.
      try {
        const parsed = new URL(url);
        return parsed.username === "" && parsed.password === "";
      } catch {
        return false;
      }
    },
    {
      message:
        "An MCP server URL must not carry a username or password; use header or OAuth authentication"
    }
  )
  .transform(canonicalizeMcpServerUrl);
